import React, { useEffect, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { GoPencil } from "react-icons/go";
import { BsTrash3 } from "react-icons/bs";
import AddDefaultPricing from "./AddDefaultPricing";
import { confirmation, errorMsg, success } from "../../../utils/notifications";
import {
  deleteDefaultPricing,
  getAllDefaultPricings,
} from "../../../api/settings.api";
import { RotatingLines } from "react-loader-spinner";

const DefaultPricing = () => {
  const [addDefaultPricingOpen, setAddDefaultPricingOpen] = useState(false);
  const [defaultPricings, setDefaultPricings] = useState([]);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDefaultPricings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllDefaultPricings();
      setDefaultPricings(response.data || []);
    } catch (error) {
      console.error("Error fetching default pricings:", error);
      setError("Failed to load default pricings. Please try again.");
      setDefaultPricings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDefaultPricings();
  }, []);

  const handleDelete = async (id) => {
    confirmation("default pricing").then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteDefaultPricing(id);
          setDefaultPricings(
            defaultPricings.filter((price) => price._id !== id)
          );
          success("Success!", "Default pricing deleted successfully!");
        } catch (error) {
          console.error("Error deleting default pricing:", error);

          const errorMessage =
            error?.response?.data?.message ||
            "An unexpected error occurred while deleting the default pricing.";

          errorMsg("Error!", errorMessage);
        }
      }
    });
  };

  return (
    <>
      <AddDefaultPricing
        open={addDefaultPricingOpen}
        setOpen={setAddDefaultPricingOpen}
        fetchDefaultPricings={fetchDefaultPricings}
        editData={editData}
        setEditData={setEditData}
      />

      <div className="mb-5 flex justify-end sticky top-16 bg-slate-50 py-3 z-10 px-2">
        <button
          onClick={() => {
            setAddDefaultPricingOpen(true);
            setEditData(null);
          }}
          className="flex items-center justify-center rounded-md bg-yellowColor px-3 py-2.5 text-sm sm:text-base font-semibold text-gray-700 shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellowColor"
        >
          <FiPlus className="text-lg mr-1" />
          Add Default Pricing
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <RotatingLines
            strokeColor="black"
            strokeWidth="5"
            animationDuration="0.75"
            width="96"
            visible={true}
          />
        </div>
      ) : error ? (
        <div className="text-center font-bold text-red-600 text-xl my-4">
          {error}
        </div>
      ) : (
        <div className="mt-5 flow-root">
          {defaultPricings.length === 0 ? (
            <div className="text-center font-bold text-gray-600 text-xl my-4">
              No default pricing added.
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Price Per Week (€)
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Created At
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Status
                    </th>
                    <th className="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-gray-900 sm:pr-6">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {defaultPricings.map((pricing) => (
                    <tr key={pricing._id}>
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-base text-gray-900 sm:pl-6">
                        {pricing.pricePerWeek} €
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {new Date(pricing.createdAt).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            pricing.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {pricing.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-6">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => {
                              setEditData(pricing);
                              setAddDefaultPricingOpen(true);
                            }}
                            className="h-9 w-9 flex items-center justify-center bg-green-100 text-green-700 rounded-md"
                          >
                            <GoPencil />
                          </button>
                          <button
                            onClick={() => handleDelete(pricing._id)}
                            className="h-9 w-9 flex items-center justify-center bg-red-100 text-red-700 rounded-md"
                          >
                            <BsTrash3 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DefaultPricing;
